import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { X } from 'lucide-react';
import type { Academy } from '../types';

interface AcademyFormProps {
  onClose: () => void;
  onSave: (academy: Academy) => void;
}

const REGIONS = [
  '서울특별시',
  '부산광역시',
  '대구광역시',
  '인천광역시',
  '광주광역시',
  '대전광역시',
  '울산광역시',
  '세종특별자치시',
  '경기도',
  '강원도',
  '충청북도',
  '충청남도',
  '전라북도',
  '전라남도',
  '경상북도',
  '경상남도',
  '제주특별자치도'
];

const AcademyForm: React.FC<AcademyFormProps> = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    region: ''
  });
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${import.meta.env.VITE_NAVER_CLIENT_ID}&submodules=geocoder`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleAddressSearch = (query: string) => {
    if (!query.trim() || !window.naver) return;

    window.naver.maps.Service.geocode({
      query: formData.region ? `${formData.region} ${query}` : query
    }, function(status: any, response: any) {
      if (status === window.naver.maps.Service.Status.ERROR) {
        return alert('검색 중 오류가 발생했습니다.');
      }

      if (response.v2.meta.totalCount > 0) {
        const suggestions = response.v2.addresses
          .filter((address: any) => {
            if (!formData.region) return true;
            return address.roadAddress.startsWith(formData.region) ||
                   address.jibunAddress.startsWith(formData.region);
          });
        setAddressSuggestions(suggestions);
      }
    });
  };

  const handleAddressSelect = (address: any) => {
    setFormData(prev => ({
      ...prev,
      address: address.roadAddress || address.jibunAddress
    }));
    setAddressSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('로그인이 필요합니다.');
        return;
      }

      const { data, error } = await supabase
        .from('academies')
        .insert([
          {
            name: formData.name,
            address: formData.address,
            user_id: user.id
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error saving academy:', error);
        alert('학원 정보 저장 중 오류가 발생했습니다.');
        return;
      }

      if (data) {
        onSave(data);
        onClose();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('학원 정보 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold mb-6">학원 정보 입력</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">학원 이름</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">지역</label>
            <select
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={formData.region}
              onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
            >
              <option value="">지역 선택</option>
              {REGIONS.map(region => (
                 <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">주소</label>
            <div className="relative">
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={formData.address}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, address: e.target.value }));
                  handleAddressSearch(e.target.value);
                }}
              />
              {addressSuggestions.length > 0 && (
                <div className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleAddressSelect(suggestion)}
                    >
                      {suggestion.roadAddress || suggestion.jibunAddress}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              저장
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcademyForm;